// Блок одного варианта: шапка (цвет, инлайн-имя, сводка, замок, аккордеон, меню),
// таймлайн, итоговая панель. Сам блок — drop-зона HTML5 DnD (data-drop-variant),
// индекс вставки вычисляется по центрам карточек (см. src/ui/dnd/geometry.ts) —
// тем же способом, что и тач-фолбэк.
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { type FlightSegment, type TripVariant, type VariantTotals } from '../../types';
import { useActions, useStore, VARIANT_COLORS } from '../../store';
import { useT } from '../../i18n';
import { useAuth } from '../../auth';
import { PriceRefreshButton } from '../../prices';
import { ConfirmDialog } from '../forms';
import { CloneIcon, SEGMENT_DND_MIME } from '../library';
import { useIsMobile } from '../shell';
import { computeInsertIndex, resolveDrop, useDnd, type DragPayload } from '../dnd';
import { Timeline } from './Timeline';
import { VariantTotalsPanel } from './VariantTotalsPanel';
import { VariantSummaryChips } from './VariantSummaryChips';
import { computeVariantSummary } from './variantSummary';
import styles from './VariantColumn.module.css';

export interface VariantColumnProps {
  variant: TripVariant;
  totals: VariantTotals;
  /** позиция варианта на доске — для кнопок перемещения */
  index: number;
  totalCount: number;
}

function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      {/* Открытый замок — та же дужка, откинутая вправо: смена состояния читается по силуэту. */}
      {locked ? <path d="M8 11V7a4 4 0 0 1 8 0v4" /> : <path d="M8 11V7a4 4 0 0 1 7.5-2" />}
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export function VariantColumn({ variant, totals, index, totalCount }: VariantColumnProps) {
  const state = useStore();
  const actions = useActions();
  const dnd = useDnd();
  const t = useT();
  const { isGuest, maxVariants } = useAuth();
  const isMobile = useIsMobile();
  const columnRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(variant.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const locked = variant.locked === true;
  const atMax = state.variants.length >= maxVariants;
  const expanded = variant.collapsed !== true;
  const summary = useMemo(() => computeVariantSummary(variant, state), [variant, state]);

  // Рейсы варианта в порядке таймлайна: кнопка в шапке проверяет цены сразу по
  // всему варианту, а под замком тоже работает — цену она никуда не ставит.
  const flights = useMemo(
    () =>
      variant.segmentIds
        .map((id) => state.library[id])
        .filter((seg): seg is FlightSegment => seg?.kind === 'flight'),
    [variant.segmentIds, state.library],
  );

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  // Замок посреди переименования: поле надо закрыть, иначе onBlur попробует
  // применить правку, которую reducer уже не примет.
  useEffect(() => {
    if (locked) setRenaming(false);
  }, [locked]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || menuBtnRef.current?.contains(t)) return;
      setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [menuOpen]);

  function startRename() {
    setNameDraft(variant.name);
    setRenaming(true);
  }

  function commitRename() {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== variant.name) actions.renameVariant(variant.id, trimmed);
    setRenaming(false);
  }

  function cancelRename() {
    setNameDraft(variant.name);
    setRenaming(false);
  }

  function onNameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  }

  /** У свёрнутого варианта карточек на экране нет — класть можно только в конец. */
  function insertIndexAt(clientY: number): number {
    if (!expanded || !columnRef.current) return variant.segmentIds.length;
    return computeInsertIndex(columnRef.current, clientY);
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    // Заблокированный вариант не перехватывает событие: пусть всплывает до фона
    // рабочей области, иначе поверх замка появлялась бы ложная зона вставки.
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    dnd.setOver({ variantId: variant.id, index: insertIndexAt(e.clientY) });
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    const related = e.relatedTarget;
    if (columnRef.current && related instanceof Node && columnRef.current.contains(related)) return;
    if (dnd.over?.variantId === variant.id) dnd.setOver(null);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    const segmentId = e.dataTransfer.getData(SEGMENT_DND_MIME) || e.dataTransfer.getData('text/plain');
    const payload: DragPayload | null =
      dnd.drag ?? (segmentId ? { segmentId, fromVariantId: null, fromIndex: null } : null);
    if (!payload) {
      dnd.end();
      return;
    }
    // Не `index`: так называется проп с позицией варианта на доске.
    const dropIndex =
      dnd.over?.variantId === variant.id ? dnd.over.index : insertIndexAt(e.clientY);
    resolveDrop(actions, payload, { variantId: variant.id, index: dropIndex });
    dnd.end();
  }

  /**
   * Пустое место шапки работает как сама стрелка аккордеона — по варианту
   * попасть проще, чем по кнопке в 28px. Всё, у чего есть собственное действие
   * (стрелка, имя, кнопки справа, раскрытое меню), клик до сюда не пускает,
   * иначе одно нажатие делало бы два дела сразу.
   */
  function onHeaderClick(e: ReactMouseEvent<HTMLElement>) {
    const target = e.target as HTMLElement;
    if (target.closest('button, input, select, a, [role="menu"]')) return;
    actions.setVariantCollapsed(variant.id, expanded);
  }

  const isDropTarget = !locked && dnd.over?.variantId === variant.id;

  return (
    <div
      ref={columnRef}
      className={`${styles.column} ${isDropTarget ? styles.dropTarget : ''} ${locked ? styles.locked : ''}`}
      // Цвет варианта живёт на колонке целиком: из него рисуются и обводка,
      // и точка в шапке — иначе цвет пришлось бы задавать в двух местах.
      style={{ '--variant-color': variant.color } as CSSProperties}
      data-drop-variant={variant.id}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Шапка — ровно одна строка из трёх зон: слева прибиты аккордеон и
          название, справа — действия, а сводка между ними уезжает в свою
          горизонтальную прокрутку. Раньше шапка переносилась, и высота колонки
          прыгала от длины названия и количества чипов. */}
      <header className={styles.header} onClick={onHeaderClick}>
        <div className={styles.headerLead}>
          <button
            type="button"
            className={styles.collapseBtn}
            aria-expanded={expanded}
            aria-controls={`variant-body-${variant.id}`}
            aria-label={t(expanded ? 'variant.collapse' : 'variant.expand')}
            title={t(expanded ? 'variant.collapse' : 'variant.expand')}
            onClick={() => actions.setVariantCollapsed(variant.id, expanded)}
          >
            <ChevronIcon open={expanded} />
          </button>

          <span className={styles.dot} aria-hidden="true" />

          {renaming ? (
            <input
              ref={inputRef}
              className={`input ${styles.nameInput}`}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={onNameKeyDown}
              onBlur={commitRename}
              aria-label={t('variant.name')}
            />
          ) : (
            <button
              type="button"
              className={styles.nameBtn}
              onClick={startRename}
              disabled={locked}
              title={t(locked ? 'variant.locked' : 'variant.rename')}
            >
              {variant.name}
            </button>
          )}
        </div>

        <div className={styles.headerScroll}>
          <VariantSummaryChips summary={summary} />
        </div>

        <div className={styles.headerActions}>
          {/* На телефоне — одна иконка: подпись с числом рейсов съедала всю
              строку шапки, и от названия варианта не оставалось ничего. */}
          <PriceRefreshButton segments={flights} compact={isMobile} className={styles.priceBtn} />

          <div className={styles.moveGroup} role="group" aria-label={t('variant.order')}>
            <button
              type="button"
              className={`btn btn-sm ${styles.moveBtn}`}
              aria-label={t('variant.moveUp')}
              title={t('variant.moveUp')}
              disabled={index === 0}
              onClick={() => actions.moveVariant(index, index - 1)}
            >
              ↑
            </button>
            <button
              type="button"
              className={`btn btn-sm ${styles.moveBtn}`}
              aria-label={t('variant.moveDown')}
              title={t('variant.moveDown')}
              disabled={index === totalCount - 1}
              onClick={() => actions.moveVariant(index, index + 1)}
            >
              ↓
            </button>
          </div>

          <button
            type="button"
            className={`btn btn-sm ${styles.iconBtn}`}
            aria-label={t('variant.clone')}
            title={
              atMax
                ? t(isGuest ? 'variant.add.guest' : 'variant.add.max', { count: maxVariants })
                : t('variant.clone')
            }
            disabled={atMax}
            onClick={() =>
              actions.duplicateVariant(variant.id, t('variant.cloneName', { name: variant.name }))
            }
          >
            <CloneIcon size={14} />
          </button>

          <button
            type="button"
            className={`btn btn-sm ${styles.iconBtn} ${locked ? styles.lockBtnOn : ''}`}
            aria-pressed={locked}
            aria-label={t(locked ? 'variant.unlock' : 'variant.lock')}
            title={t(locked ? 'variant.unlock' : 'variant.lock')}
            onClick={() => actions.setVariantLocked(variant.id, !locked)}
          >
            <LockIcon locked={locked} />
          </button>

          <div className={styles.headerMenuWrap}>
            <button
              ref={menuBtnRef}
              type="button"
              className={`btn btn-sm ${styles.menuBtn}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t('variant.menu')}
              disabled={locked}
              title={locked ? t('variant.locked') : undefined}
              onClick={() => setMenuOpen((v) => !v)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div ref={menuRef} role="menu" className={styles.menu}>
                <div className={styles.menuLabel}>{t('variant.color')}</div>
                <div className={styles.palette} role="group" aria-label={t('variant.color')}>
                  {VARIANT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="menuitemradio"
                      aria-checked={variant.color === c}
                      aria-label={t('variant.colorOf', { color: c })}
                      className={`${styles.swatch} ${variant.color === c ? styles.swatchActive : ''}`}
                      style={{ background: c }}
                      onClick={() => {
                        actions.setVariantColor(variant.id, c);
                        setMenuOpen(false);
                      }}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className={`btn btn-sm btn-danger ${styles.deleteBtn}`}
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(true);
                  }}
                >
                  {t('variant.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {expanded && (
        <div id={`variant-body-${variant.id}`} className={styles.body}>
          <Timeline variant={variant} totals={totals} />
          <VariantTotalsPanel totals={totals} />
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('variant.delete.title')}
        message={t('variant.delete.body', { name: variant.name })}
        confirmLabel={t('library.delete')}
        danger
        onConfirm={() => {
          setConfirmDelete(false);
          actions.deleteVariant(variant.id);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
